import { pure } from '@core/helpers/pure';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  output,
  OutputEmitterRef,
  OutputRefSubscription,
  signal,
  ViewChild,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  TuiDataList,
  TuiError,
  TuiIcon,
  TuiButton,
  TuiInput,
  TuiTextfield,
  TuiCalendar,
} from '@taiga-ui/core';
import {
  TuiDataListWrapper,
  TuiUnmaskHandler,
  TuiSelect,
  TuiInputDate,
} from '@taiga-ui/kit';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  distinctUntilChanged,
  EMPTY,
  interval,
  merge,
  of,
  Subject,
  switchMap,
  take,
  takeWhile,
  tap,
} from 'rxjs';
import type { Nullable } from '@core/helpers/nullable';
import { HyphenedStringTuiDateTransformerDirective } from '@core/directives/hyphenedStringTuiDateTransformer.directive';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { AsyncPipe, DatePipe } from '@angular/common';
import { RequestService } from '@core/services/request/request.service';
import { RequestDeviceProtectiveCaseInputComponent } from '@features/requests';
import { RequestDeviceProtectiveCaseFormFields } from '@features/requests/request-form/components/request-form-devices/components/request-device-protective-case-input/types';
import type { ProtectiveCaseFormControl } from '@features/requests/request-form/components/request-form-devices/components/request-device-protective-case-input/types';
import { TranslatePipe } from '@ngx-translate/core';
import { DeviceDetectorService } from '@core/services';
import { RequestDevicesService } from '@core/services/request/request-devices.service';
import {
  RequestDeviceMountType,
  RequestDeviceOnboardVoltage,
} from '@core/types';
import { WaGeolocationService } from '@ng-web-apis/geolocation';

enum FormFields {
  Sim1 = 'sim1',
  Sim2 = 'sim2',
  ProtectiveCaseSubform = 'protectiveCase',
  DeviceInstallDate = 'deviceInstallDate',
  MountType = 'mountType',
  OnboardVoltage = 'onboardVoltage',
}

@Component({
  selector: 'x-request-form-devices-request-device-adding',
  imports: [
    TuiTextfield,
    TuiCalendar,
    AsyncPipe,
    ReactiveFormsModule,
    TuiError,
    TuiButton,
    TuiIcon,
    HyphenedStringTuiDateTransformerDirective,
    TuiInputDate,
    RequestDeviceProtectiveCaseInputComponent,
    TuiInput,
    TranslatePipe,
    TuiUnmaskHandler,
    TuiDataList,
    TuiDataListWrapper,
    TuiSelect,
  ],
  providers: [DatePipe],
  templateUrl: './request-form-devices-request-device-adding.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestFormDevicesRequestDeviceAddingComponent
  implements OnInit, OnDestroy
{
  $outsideToggleAddDeviceForm = input<OutputEmitterRef<void>>(undefined, {
    alias: 'outsideToggleAddDeviceForm',
  });
  $isInsideSummary = input<boolean>(false, {
    alias: 'isInsideSummary',
  });
  private readonly formBuilder = inject(FormBuilder);
  private readonly geolocation$ = inject(WaGeolocationService);
  private readonly requestService = inject(RequestService);
  private readonly requestDevicesService = inject(RequestDevicesService);
  private readonly deviceService = inject(DeviceDetectorService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly injector = inject(Injector);
  readonly permissionService = inject(OutkeepPermissionService);

  readonly $request = this.requestService.$request;

  protected readonly $isMobile = this.deviceService.$isMobile;

  /**
   * true - устройство добавлено в заявку
   * false - пользователь нажал кнопку "Отмена"
   */
  formClosed = output<boolean>();

  FormFields = FormFields;

  formGroup = this.formBuilder.group({
    [FormFields.Sim1]: this.formBuilder.control<Nullable<string>>(null, [
      Validators.required,
      Validators.pattern('^[0-9]*$'),
    ]),
    [FormFields.Sim2]: this.formBuilder.control<Nullable<string>>(null, [
      Validators.pattern('^[0-9]*$'),
    ]),
    [FormFields.ProtectiveCaseSubform]: null,
    [FormFields.DeviceInstallDate]: this.formBuilder.control<Date>(new Date(), {
      validators: [Validators.required],
      nonNullable: true,
    }),
    [FormFields.MountType]:
      this.formBuilder.control<Nullable<RequestDeviceMountType>>(null),

    [FormFields.OnboardVoltage]:
      this.formBuilder.control<Nullable<RequestDeviceOnboardVoltage>>(null),
  });

  $isAddNewDeviceFormShown = signal(false);

  addNewDeviceToRequest$ = new Subject<void>();

  private emitterSub: Nullable<OutputRefSubscription> = null;

  @ViewChild('addDeviceForm') addNewDeviceFormContainer!: ElementRef;
  readonly simMask = {
    mask: [
      /\d/,
      ' ',
      /\d/,
      /\d/,
      /\d/,
      ' ',
      /\d/,
      /\d/,
      /\d/,
      '-',
      /\d/,
      /\d/,
      '-',
      /\d/,
      /\d/,
    ],
  };
  readonly unmaskSim = (value: string): string => value.replace(/[-\s]/g, '');

  constructor() {
    effect(() => {
      if (this.$outsideToggleAddDeviceForm()) {
        this.emitterSub = this.$outsideToggleAddDeviceForm()!.subscribe(() => {
          this.$isAddNewDeviceFormShown.set(true);
          setTimeout(() => {
            this.addNewDeviceFormContainer?.nativeElement.scrollIntoView({
              behavior: 'smooth',
            });
          }, 300);
        });
      }
    });
  }

  ngOnInit() {
    this.initDeviceInstallDateInterval();

    this.addNewDeviceToRequest$
      .pipe(
        switchMap(() => this.performAddNewDeviceToRequest$()),
        // switchMap(() =>
        //   this.requestDevicesService.checkWarehouseDeviceSoldAttempt$(
        //     this.sim1Field.value!,
        //   ),
        // ),
        tap(() => {
          this.requestService.reloadRequest();
          this.formGroup.reset();
          this.formClosed.emit(true);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // to show a validation message as soon as a user starts typing
    merge(this.sim1Field.valueChanges, this.sim2Field.valueChanges)
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.sim1Field.markAsTouched();
        this.sim2Field.markAsTouched();
      });

    toObservable(this.$isAddNewDeviceFormShown, {
      injector: this.injector,
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(shown => {
        if (shown)
          this.geolocation$
            .pipe(take(1))
            .subscribe(() => console.log('Pre-cached geolocation'));
      });
  }

  ngOnDestroy(): void {
    if (this.emitterSub) {
      this.emitterSub.unsubscribe();
      this.emitterSub = null;
    }
  }

  @pure
  get sim1Field() {
    return this.formGroup.controls[FormFields.Sim1];
  }

  @pure
  get sim2Field() {
    return this.formGroup.controls[FormFields.Sim2];
  }

  @pure
  get protectiveCaseSubformField(): ProtectiveCaseFormControl {
    return this.formGroup.controls[FormFields.ProtectiveCaseSubform];
  }

  get hasProtectiveCaseValue(): boolean {
    return this.protectiveCaseSubformField.value
      ? this.protectiveCaseSubformField.value[
          RequestDeviceProtectiveCaseFormFields.HasProtectiveCase
        ]
      : false;
  }

  get protectiveCaseNumberValue(): Nullable<number> {
    return this.protectiveCaseSubformField.value
      ? this.protectiveCaseSubformField.value[
          RequestDeviceProtectiveCaseFormFields.ProtectiveCaseNumber
        ]
      : null;
  }

  @pure
  get deviceInstallDateField() {
    return this.formGroup.controls[FormFields.DeviceInstallDate];
  }

  performAddNewDeviceToRequest$() {
    if (this.formGroup.valid) {
      return this.requestDevicesService
        .addNewDeviceToRequest$(
          {
            installDate: this.deviceInstallDateField.value,
            name: this.sim1Field.value!,
            secondSim: this.sim2Field.value,
            hasProtectCase: this.hasProtectiveCaseValue,
            protectCaseNumber: this.protectiveCaseNumberValue,
          },
          this.$isMobile(),
        )
        .pipe(
          tap(() => {
            this.$isAddNewDeviceFormShown.set(false);
          }),
        );
    }

    return EMPTY;
  }

  cancelAddingNewRequestDevice() {
    this.$isAddNewDeviceFormShown.set(false);
    this.formGroup.reset();
    this.formClosed.emit(false);
  }

  doesCurrentRequestHasDevicesInProducts(): boolean {
    return (this.$request().products || []).some(requestProduct => {
      const lowercasedProductName =
        requestProduct.requestProductId.name.toLowerCase();

      return (
        lowercasedProductName.includes('x-keeper invis duos') ||
        lowercasedProductName.includes('установка оборудования') ||
        lowercasedProductName.includes('installation of equipment') ||
        lowercasedProductName.includes('ekipman kurulumu') ||
        lowercasedProductName.includes('x-keeper obd')
      );
    });
  }

  /**
   * Каждый час обновляет значение поля "Даты установки устройства" на "сегодня"
   */
  private initDeviceInstallDateInterval() {
    const oneHour = 1000 * 60 * 60;

    toObservable(this.$isAddNewDeviceFormShown, { injector: this.injector })
      .pipe(
        distinctUntilChanged(),
        switchMap(value => {
          if (value) {
            return interval(oneHour).pipe(
              tap(() => {
                this.deviceInstallDateField.setValue(new Date());
              }),
              takeWhile(() => this.$isAddNewDeviceFormShown()),
            );
          }

          return of(value);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public showDeviceAddingForm() {
    this.$isAddNewDeviceFormShown.set(true);
    setTimeout(() => {
      this.addNewDeviceFormContainer?.nativeElement.scrollIntoView({
        behavior: 'smooth',
      });
    }, 300);
  }
}
